import { useEffect, useState, useCallback } from 'react';
import { updateUrlParams } from '../utils/url';

const STORAGE_KEY = 'selected_location_id';
const QUERY_KEY = 'co';

export function getStoredLocationId(): string | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v ? JSON.parse(v) : null;
  } catch {
    return null;
  }
}

export function setStoredLocationId(locationId: string | null) {
  try {
    if (locationId) localStorage.setItem(STORAGE_KEY, JSON.stringify(locationId));
    else localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new CustomEvent('locationSelected', { detail: { locationId } }));
  } catch {}
}

// Returns a { locationId, select } pair for compatibility with existing component props
export function useLocationSelection(defaultLocationId?: string | null) {
  const getInitial = () => {
    if (typeof window === 'undefined') return defaultLocationId || null;
    try {
      const params = new URLSearchParams(window.location.search);
      const fromUrl = params.get(QUERY_KEY);
      if (fromUrl && fromUrl.toLowerCase() !== 'all') return fromUrl;
    } catch {}
    return getStoredLocationId() || defaultLocationId || null;
  };

  const [locationId, setLocationId] = useState<string | null>(() => getInitial());

  // An address that names a location is the choice: keep it (see useAccountSelection).
  useEffect(() => {
    try {
      const fromUrl = new URLSearchParams(window.location.search).get(QUERY_KEY);
      if (fromUrl && fromUrl.toLowerCase() !== 'all' && getStoredLocationId() !== fromUrl) setStoredLocationId(fromUrl);
    } catch {}
  }, []);

  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        try {
          setLocationId(e.newValue ? JSON.parse(e.newValue) : null);
        } catch {
          setLocationId(null);
        }
      }
    };
    const customHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail && 'locationId' in detail) setLocationId(detail.locationId);
    };
    window.addEventListener('storage', handler);
    window.addEventListener('locationSelected', customHandler as EventListener);
    // Back/forward adopt a location only when the address names one. A page without ?co=
    // (Executive, Reports, My Work) keeps the last location opened, so
    // Departments and Reports start from it.
    const onPopState = () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const fromUrl = params.get(QUERY_KEY);
        if (!fromUrl || fromUrl.toLowerCase() === 'all') return;
        setLocationId(fromUrl);
        setStoredLocationId(fromUrl);
      } catch {}
    };
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('storage', handler);
      window.removeEventListener('locationSelected', customHandler as EventListener);
      window.removeEventListener('popstate', onPopState);
    };
  }, []);

  const select = useCallback((id: string | null) => {
    setLocationId(id);
    setStoredLocationId(id);
    try {
      if (id && id.toLowerCase() !== 'all') {
        updateUrlParams({ [QUERY_KEY]: id });
      } else {
        updateUrlParams({ [QUERY_KEY]: null });
      }
    } catch {}
  }, []);

  return { locationId, select };
}
